import type { ChangeEventHandler, FocusEventHandler, HTMLAttributes, ReactNode, RefCallback } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ClassValue, clsx } from 'clsx';

import { cn } from '../../lib/utils';
import { FieldError } from '../FieldError';

export interface TextAreaFieldProps {
    id?: string;
    name?: string;
    label?: ReactNode; // Preferably a string for keeping styles consistent
    defaultValue?: string;
    placeholder?: string;
    description?: string;
    rows?: number;
    className?: ClassValue;
    dataTestid?: string;
    labelHtmlAttributes?: HTMLAttributes<HTMLLabelElement>;
    inputHtmlAttributes?: HTMLAttributes<HTMLTextAreaElement>;
    required?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    inputRef?: RefCallback<HTMLTextAreaElement>;
    onChange: ChangeEventHandler<HTMLTextAreaElement>;
    onBlur?: FocusEventHandler<HTMLTextAreaElement>;
    value?: string;
    icon?: IconDefinition;
}

export const TextAreaField = ({
    id,
    name,
    label = '',
    defaultValue = '',
    placeholder = '',
    description = '',
    rows = 3,
    className,
    dataTestid,
    labelHtmlAttributes,
    inputHtmlAttributes,
    required = false,
    disabled = false,
    errorMessage = '',
    inputRef,
    onChange,
    onBlur,
    value,
    icon,
}: TextAreaFieldProps) => {
    const isControlled = value !== undefined;

    return (
        <div
            data-component-name='TextAreaField'
            className={cn('flex w-full gap-3', className)}
        >
            <div className='flex-1'>
                {label && (
                    <label
                        htmlFor={id}
                        className={clsx([
                            'mb-2 block text-xs font-medium leading-4',
                            disabled ? 'text-gray-400' : 'text-gray-800',
                            { 'ml-9': icon },
                        ])}
                        aria-disabled={disabled}
                        data-testid={dataTestid && `${dataTestid}-label`}
                        {...labelHtmlAttributes}
                    >
                        {label} {required && '*'}
                    </label>
                )}
                <div className='flex gap-3'>
                    {icon && (
                        <FontAwesomeIcon
                            className={clsx('relative py-2.5 w-6 text-gray-500', {})}
                            icon={icon}
                        />
                    )}
                    <div
                        className={clsx([
                            'flex rounded-md bg-white w-full ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset',
                            disabled ? 'bg-gray-100' : '',
                            {
                                'ring-red-300 focus-within:ring-red-300': errorMessage,
                                'ring-gray-300 focus-within:ring-blue-300': !errorMessage,
                            },
                        ])}
                    >
                        <textarea
                            id={id}
                            name={name}
                            className={clsx([
                                'block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-800 placeholder:text-gray-500 focus:ring-0 sm:text-sm sm:leading-6',
                                'disabled:cursor-not-allowed disabled:text-gray-400 disabled:placeholder:text-gray-400',
                            ])}
                            placeholder={placeholder}
                            disabled={disabled}
                            required={required}
                            rows={rows}
                            data-testid={dataTestid && `${dataTestid}-input`}
                            {...(isControlled ? { value } : { defaultValue })}
                            ref={inputRef}
                            onChange={onChange}
                            onBlur={onBlur}
                            {...inputHtmlAttributes}
                            onWheel={(e) => e.currentTarget.blur()}
                        />
                    </div>
                </div>

                {description && (
                    <p
                        className='mt-2 text-sm'
                        data-testid={dataTestid && `${dataTestid}-description`}
                    >
                        {description}
                    </p>
                )}

                {errorMessage && (
                    <FieldError dataTestid={dataTestid && `${dataTestid}-error`}>{errorMessage}</FieldError>
                )}
            </div>
        </div>
    );
};
